import React, { createContext, useContext, useEffect, useMemo, useRef, useState, ReactNode } from 'react';
import { apiService } from '../services/api';

export interface AuthUser {
	user_id: string;
	email: string;
	display_name: string | null;
	first_name: string | null;
	last_name: string | null;
	status: 'active' | 'suspended';
}

interface AuthContextValue {
	user: AuthUser | null;
	isAuthenticated: boolean;
	isLoading: boolean;
	requestMagicLink: (email: string) => Promise<string | undefined>;
	updateProfile: (firstName: string, lastName: string) => Promise<AuthUser>;
	consumeMagicLink: (token: string) => Promise<AuthUser>;
	logout: () => Promise<void>;
	checkAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const useAuth = (): AuthContextValue => {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error('useAuth must be used within an AuthProvider');
	}
	return context;
};

interface AuthProviderProps {
	children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
	const [user, setUser] = useState<AuthUser | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const lastAuthMutationAtRef = useRef(0);

	const checkAuth = async () => {
		const startedAt = Date.now();
		setIsLoading(true);
		const result = await apiService.getCurrentUser();
		if (lastAuthMutationAtRef.current > startedAt) {
			setIsLoading(false);
			return;
		}
		if (result.success && result.data?.user) {
			setUser(result.data.user);
		} else {
			setUser(null);
		}
		setIsLoading(false);
	};

	useEffect(() => {
		checkAuth().catch(() => {
			setUser(null);
			setIsLoading(false);
		});
	}, []);

	const requestMagicLink = async (email: string): Promise<string | undefined> => {
		const result = await apiService.requestMagicLink(email);
		if (!result.success) {
			throw new Error(result.error || 'Failed to request magic link');
		}
		return result.data?.senderAddress;
	};

	const consumeMagicLink = async (token: string) => {
		const result = await apiService.consumeMagicLink(token);
		if (!result.success || !result.data?.user) {
			throw new Error(result.error || 'Failed to consume magic link');
		}
		lastAuthMutationAtRef.current = Date.now();
		setUser(result.data.user);
		return result.data.user;
	};

	const updateProfile = async (firstName: string, lastName: string) => {
		const result = await apiService.updateProfile(firstName, lastName);
		if (!result.success || !result.data?.user) {
			throw new Error(result.error || 'Failed to update profile');
		}
		lastAuthMutationAtRef.current = Date.now();
		setUser(result.data.user);
		return result.data.user;
	};

	const logout = async () => {
		await apiService.logout();
		lastAuthMutationAtRef.current = Date.now();
		setUser(null);
	};

	const value = useMemo(() => ({
		user,
		isAuthenticated: Boolean(user),
		isLoading,
		requestMagicLink,
		updateProfile,
		consumeMagicLink,
		logout,
		checkAuth,
	}), [user, isLoading]);

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
